import type { VisibleSensorInfo } from 'features/timeSeries/components/rulerTool/useRulerTool'

export interface LabelRange {
  startMs: number
  endMs: number
}

export interface PlotHorizontalBounds {
  overLeft: number
  overRight: number
}

export interface ProjectedLabelSpan {
  startPx: number
  endPx: number
  horizontallyVisible: boolean
}

export interface PenScopeOption {
  sensorId: string
  name: string
  color: string | null
  /** False for a pen the label still scopes that is no longer plotted. */
  onChart: boolean
}

export function normalizeRange(aMs: number, bMs: number): LabelRange {
  return { startMs: Math.min(aMs, bMs), endMs: Math.max(aMs, bMs) }
}

/**
 * Project a label's persisted time range to on-overlay pixels.
 *
 * Unlike a ruler, a label carries no pixel fields at all — the time range is
 * the only durable truth — so with no live plot to project against there is
 * nothing to draw, and the span reports itself invisible rather than
 * collapsing to pixel 0.
 */
export function projectLabelSpan(
  range: LabelRange,
  valToPos: ((value: number, scale: string) => number) | null,
  bounds: PlotHorizontalBounds | null
): ProjectedLabelSpan {
  if (!valToPos || !bounds) {
    return { startPx: 0, endPx: 0, horizontallyVisible: false }
  }

  const startPx = valToPos(range.startMs, 'x') + bounds.overLeft
  const endPx = valToPos(range.endMs, 'x') + bounds.overLeft

  if (!Number.isFinite(startPx) || !Number.isFinite(endPx)) {
    return { startPx: 0, endPx: 0, horizontallyVisible: false }
  }

  return {
    startPx,
    endPx,
    horizontallyVisible: !(
      endPx < bounds.overLeft || startPx > bounds.overRight
    ),
  }
}

/** Width of the flag's triangular point. Text is padded clear of it. */
export const TITLE_FLAG_TIP_WIDTH = 8

/**
 * Everything the flag adds around its measured text: 7px of left padding, and
 * 7px plus the tip on the right.
 */
export const TITLE_FLAG_CHROME_PX = 14 + TITLE_FLAG_TIP_WIDTH

/** Flag box (15px line + 2px padding) plus the space between two lanes. */
export const TITLE_LANE_STRIDE = 22

/** Gap above the first lane, inside the plot area. */
export const TITLE_TOP_INSET = 4

/** Clear space between two flags sharing a lane. */
export const TITLE_LANE_GAP = 6

export interface LabelFlagPlacement {
  left: number
  visible: boolean
}

/**
 * Where a title flag starts: at its band's projected left edge, so the flag
 * reads as flying from the region it names. It is never centered on the band
 * and never sized by it — a flag runs past its own band whenever the title is
 * longer than the region is wide, which is the common case.
 *
 * A band whose start has been panned off the left of the plot keeps its flag
 * pinned to the plot edge rather than sliding out of sight with it.
 */
export function getLabelFlagPlacement({
  startPx,
  endPx,
  width,
  plotLeft,
  plotRight,
  keepInView = false,
}: {
  startPx: number
  endPx: number
  width: number
  plotLeft: number
  plotRight: number
  /** Pull a flag that would overrun the right edge back into the plot. */
  keepInView?: boolean
}): LabelFlagPlacement {
  if (plotRight <= plotLeft || endPx < plotLeft || startPx > plotRight) {
    return { left: plotLeft, visible: false }
  }

  const left = Math.max(startPx, plotLeft)
  if (!keepInView) return { left, visible: true }

  return {
    left: Math.min(left, Math.max(plotRight - width, plotLeft)),
    visible: true,
  }
}

/**
 * How many lanes of flags the plot can stack. Nothing may shrink or hide any
 * more, so the only bound left on the packing is the chart's own height.
 */
export function getTitleLaneCapacity({
  plotHeight,
  topInset = TITLE_TOP_INSET,
  laneStride = TITLE_LANE_STRIDE,
}: {
  plotHeight: number
  topInset?: number
  laneStride?: number
}): number {
  if (laneStride <= 0) return 1
  return Math.max(1, Math.floor((plotHeight - topInset) / laneStride))
}

export interface LabelLaneCandidate {
  id: string
  left: number
  width: number
}

/**
 * Assign every title flag to a lane, sweeping left to right and taking the
 * first lane whose last flag ends at least `gap` before this one starts.
 *
 * Every candidate is placed: a flag that finds no free lane within `maxLanes`
 * shares the last one, overlapping what is already there. Overlap is the least
 * bad outcome left once titles may neither shrink nor disappear, and it only
 * happens when the labels outnumber the lanes the plot is tall enough to hold.
 *
 * The sweep sorts internally, so the result does not depend on the order the
 * caller happened to project its labels in.
 */
export function packLabelTitleLanes(
  candidates: LabelLaneCandidate[],
  {
    maxLanes = Number.POSITIVE_INFINITY,
    gap = TITLE_LANE_GAP,
  }: { maxLanes?: number; gap?: number } = {}
): Map<string, number> {
  const placement = new Map<string, number>()
  const laneCap = Math.max(1, Math.floor(maxLanes))
  const laneEnds: number[] = []

  const ordered = [...candidates].sort(
    (a, b) => a.left - b.left || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )

  for (const candidate of ordered) {
    let lane = laneEnds.findIndex((end) => candidate.left >= end)

    if (lane === -1) {
      lane = laneEnds.length < laneCap ? laneEnds.length : laneCap - 1
    }

    laneEnds[lane] = Math.max(
      laneEnds[lane] ?? -Infinity,
      candidate.left + candidate.width + gap
    )
    placement.set(candidate.id, lane)
  }

  return placement
}

/**
 * Move one edge of a range to `valueMs`. Dragging an edge past its opposite
 * swaps which edge is being dragged, so the gesture continues in the direction
 * the mouse is going instead of pinning at zero width.
 */
export function applyEdgeResize(
  range: LabelRange,
  edge: 'start' | 'end',
  valueMs: number
): { startMs: number; endMs: number; edge: 'start' | 'end' } {
  if (edge === 'start') {
    if (valueMs > range.endMs) {
      return { startMs: range.endMs, endMs: valueMs, edge: 'end' }
    }
    return { startMs: valueMs, endMs: range.endMs, edge: 'start' }
  }

  if (valueMs < range.startMs) {
    return { startMs: valueMs, endMs: range.startMs, edge: 'start' }
  }
  return { startMs: range.startMs, endMs: valueMs, edge: 'end' }
}

/**
 * Rows for the config panel's pen list: every pen currently plotted, plus any
 * pen the label already scopes that has since left the chart. The latter are
 * kept so the user can uncheck them — silently dropping them on save would
 * rewrite scope the user never touched.
 */
export function buildPenScopeOptions(
  chartPens: VisibleSensorInfo[],
  scopedSensorIds: string[]
): PenScopeOption[] {
  const options: PenScopeOption[] = chartPens.map((pen) => ({
    sensorId: pen.sensorId,
    name: pen.name,
    color: pen.color,
    onChart: true,
  }))

  const onChartIds = new Set(chartPens.map((pen) => pen.sensorId))
  for (const sensorId of scopedSensorIds) {
    if (onChartIds.has(sensorId)) continue
    options.push({ sensorId, name: sensorId, color: null, onChart: false })
    onChartIds.add(sensorId)
  }

  return options
}

const DAY_MS = 24 * 60 * 60 * 1000

/** The window "Go to label" opens when the label is shorter than it. */
export const DEFAULT_LABEL_VIEW_SPAN_MS = 7 * DAY_MS

/** Fraction of the label's own span left visible on each side of it. */
const LABEL_VIEW_PADDING_RATIO = 0.25

/**
 * The chart window to open for a label: the label centred in a span wide
 * enough to hold it plus context on both sides.
 *
 * A label shorter than a week gets the default week, so the surrounding
 * operation is on screen rather than just the annotated span. A longer one
 * would be clipped by that, so it widens to its own span plus padding instead
 * — the whole label is always visible.
 */
export function buildLabelViewWindow(
  range: LabelRange,
  defaultSpanMs: number = DEFAULT_LABEL_VIEW_SPAN_MS
): { fromMs: number; toMs: number } {
  const { startMs, endMs } = normalizeRange(range.startMs, range.endMs)
  const labelSpanMs = endMs - startMs
  const paddedSpanMs = labelSpanMs * (1 + 2 * LABEL_VIEW_PADDING_RATIO)
  const spanMs = Math.max(defaultSpanMs, paddedSpanMs)
  const midpointMs = startMs + labelSpanMs / 2

  return {
    fromMs: Math.round(midpointMs - spanMs / 2),
    toMs: Math.round(midpointMs + spanMs / 2),
  }
}
